// ============================================================
// Project creation presets — optional, opt-in metadata_json carriers
// for the existing New Project flow.
//
// WHY THIS EXISTS:
//   /api/projects/create already accepts a metadata_json jsonb field
//   (api/projects/create.ts), but the New Project modal historically
//   only sent name/slug/client_name/domain — so there was no way to
//   attach operator metadata at creation time. This registry lets the
//   SAME modal pre-fill fields and attach a known-good metadata_json
//   when (and only when) it is opened with ?preset=<key>. With no
//   preset, the modal behaves exactly as before (metadata_json omitted
//   → server defaults to {}). Existing projects are unaffected.
//
// SCHEMA NOTES (verified against supabase/migrations/*sq_projects* and
// api/projects/create.ts):
//   - status CHECK is ('active','paused','archived'). 'source_ready' is
//     a UI capability LABEL, never a DB status. Presets use 'active'.
//   - There is NO tags/description column. Readiness, labels, kind, and
//     description all live inside metadata_json (jsonb).
//   - slug is globally UNIQUE; a duplicate returns 400 slug_taken.
//
// HONESTY:
//   The `readiness` map below is OPERATOR-DECLARED state for the Olivium
//   subsystems, supplied by the operator. It is metadata about those
//   subsystems — it is NOT a claim that the Square Code project row is
//   live. The row is "live" only once it exists in sq_projects.
// ============================================================

export interface ProjectPreset {
  key: string
  name: string
  slug: string
  client_name?: string
  /** Must be one of active|paused|archived (DB CHECK). */
  status: 'active' | 'paused' | 'archived'
  domain?: string
  /** Attached verbatim to /api/projects/create as metadata_json (jsonb). */
  metadata_json: Record<string, unknown>
}

export const PROJECT_PRESETS: Record<string, ProjectPreset> = {
  'olivium-commandery': {
    key: 'olivium-commandery',
    name: 'Olivium Commandery',
    slug: 'olivium-commandery',
    client_name: 'Operator Control',
    status: 'active',
    domain: 'operator-control',
    metadata_json: {
      operator_only: true,
      project_kind: 'operator_control_project',
      systems: [
        'Commandery Station',
        'Secure Share',
        'Secure Shield',
        'Access codes & invites',
        'Controlled session calls',
        'Olivium backend control',
      ],
      readiness: {
        phase_4_secure_share_listing: 'live_verified',
        phase_5_gated_invite_creation: 'source_ready',
        phase_6_redemption_claim: 'source_ready',
        phase_7_session_binding: 'planned',
        controlled_session_calls: 'planned',
      },
      labels: [
        'operator_only',
        'commandery',
        'secure_share',
        'secure_shield',
        'olivium',
        'gated_write',
      ],
    },
  },
  'square-code-v2': {
    key: 'square-code-v2',
    name: 'Square Code V2',
    slug: 'square-code-v2',
    client_name: 'Square Code',
    status: 'active',
    domain: 'square-code-v2',
    metadata_json: {
      operator_only: true,
      project_kind: 'square_code_v2_command_center',
      systems: [
        'V2 Command Center',
        'V2 Projects',
        'Customer Discovery Workroom',
        'Quam Calendar Workroom',
        'Finance Spreadsheet Workroom',
        'Division Workspace Layer',
      ],
      readiness: {
        v2_main_screen: 'route_ready',
        v2_projects: 'route_ready',
        customer_discovery: 'route_ready',
        calendar: 'route_ready',
        finance_spreadsheet: 'route_ready',
        division_workspaces: 'route_ready',
        backend_wiring: 'partial',
      },
      labels: [
        'operator_only',
        'square_code_v2',
        'command_center_v2',
        'workrooms',
        'local_preview',
      ],
    },
  },
}

export function getProjectPreset(key: string | null | undefined): ProjectPreset | null {
  if (!key) return null
  return PROJECT_PRESETS[key] ?? null
}
